import { useState, useEffect, useCallback } from 'react';
import type { DomainConfig, SenderAccount } from '../layout/AccountSwitcher';
import { getRegionDomain, getCommonRegion, getRegionLabel } from '../../utils/regionMap';
import { getCountryLanguage, isEnglishCountry, getLanguagesForCountries } from '../../utils/countryLanguage';
import { authFetch } from '../../utils/authFetch';

interface Props {
    accounts: DomainConfig[];
    defaultSender: SenderAccount | null;
    onClose: () => void;
}

interface Lead {
    id: string;
    campaignId: string;
    name: string;
    title: string;
    companyName: string;
    email: string;
    country: string;
    status: string;
}

interface Campaign {
    id: string;
    name: string;
}

const PLACEHOLDERS = ['{{name}}', '{{company}}', '{{title}}', '{{country}}', '{{senderName}}', '{{senderEmail}}'];

function renderTemplate(template: string, vars: Record<string, string>): string {
    let result = template;
    for (const [key, val] of Object.entries(vars)) {
        result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val);
    }
    return result;
}

const REGION_ICONS: Record<string, string> = {
    US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', AR: '🇦🇷', UY: '🇺🇾', BR: '🇧🇷',
    CO: '🇨🇴', CL: '🇨🇱', ES: '🇪🇸', FR: '🇫🇷', DE: '🇩🇪', GB: '🇬🇧',
    AE: '🇦🇪', SG: '🇸🇬', AU: '🇦🇺', JP: '🇯🇵', KR: '🇰🇷', CN: '🇨🇳',
};

export function ComposeModal({ accounts, defaultSender, onClose }: Props) {
    const allSenders = accounts.flatMap(d => d.senders);
    const [fromEmail, setFromEmail] = useState(defaultSender?.email || allSenders[0]?.email || '');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Test email mode
    const [testMode, setTestMode] = useState(false);
    const [manualToEmail, setManualToEmail] = useState('');

    // All contacts data
    const [allContacts, setAllContacts] = useState<Lead[]>([]);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Filters
    const [countryFilter, setCountryFilter] = useState('all');
    const [campaignFilter, setCampaignFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Auto-send
    const [sendProgress, setSendProgress] = useState(0);
    const [sendTotal, setSendTotal] = useState(0);
    const [sendResults, setSendResults] = useState<{ email: string; ok: boolean; lang?: string }[]>([]);
    const [previewIdx, setPreviewIdx] = useState(0);

    // ── Active step: 'select' → 'compose' → 'preview' → 'sending' → 'done'
    const [step, setStep] = useState<'select' | 'compose' | 'preview' | 'sending' | 'done'>(
        allContacts.length === 0 && !loading ? 'select' : 'select'
    );

    const sender = allSenders.find(s => s.email === fromEmail);

    // ── Load all contacts on mount ──
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await authFetch('/campaigns');
                const data = await res.json();
                const campaignList: Campaign[] = data.campaigns || [];
                setCampaigns(campaignList);
                const all: Lead[] = [];
                for (const c of campaignList) {
                    try {
                        const lr = await authFetch(`/campaigns/${c.id}`);
                        const ld = await lr.json();
                        all.push(...(ld.leads || []));
                    } catch { }
                }
                // Dedup by email
                const emailMap = new Map<string, Lead>();
                for (const lead of all) emailMap.set(lead.email.toLowerCase(), lead);
                setAllContacts(Array.from(emailMap.values()));
            } catch { }
            setLoading(false);
        })();
    }, []);

    // ── Filtered contacts ──
    const filtered = allContacts.filter(l => {
        if (countryFilter !== 'all' && l.country !== countryFilter) return false;
        if (campaignFilter !== 'all' && l.campaignId !== campaignFilter) return false;
        if (statusFilter !== 'all' && l.status !== statusFilter) return false;
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            return l.name.toLowerCase().includes(q) ||
                l.companyName.toLowerCase().includes(q) ||
                l.email.toLowerCase().includes(q) ||
                l.title.toLowerCase().includes(q);
        }
        return true;
    });

    const selectedContacts = allContacts.filter(l => selectedIds.has(l.id));
    const countries = Array.from(new Set(allContacts.map(l => l.country))).sort();
    const statuses = Array.from(new Set(allContacts.map(l => l.status))).sort();

    // Region lock
    const commonRegion = getCommonRegion(selectedContacts.map(l => l.country));
    const isMixedRegion = selectedContacts.length > 1 && !commonRegion;

    // Filter senders by region
    const allowedSenders = commonRegion
        ? allSenders.filter(s => s.email.endsWith(`@${commonRegion}`))
        : allSenders;

    // Language info
    const targetLangs = getLanguagesForCountries(selectedContacts.map(l => l.country));
    const hasNonEnglish = targetLangs.some(l => l.code !== 'en');

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedIds.size === filtered.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filtered.map(l => l.id)));
        }
    };

    const selectByCountry = (country: string) => {
        const ids = allContacts.filter(l => l.country === country).map(l => l.id);
        setSelectedIds(new Set(ids));
    };

    // ── Auto-send ──
    const autoSend = async () => {
        // Test mode: send to manual email
        if (testMode) {
            if (!manualToEmail || !subject || !body || !fromEmail) return;
            setStep('sending');
            setSendTotal(1);
            setSendProgress(0);
            setSendResults([]);
            const vars: Record<string, string> = {
                name: '[Test Name]',
                company: '[Test Company]',
                title: '[Test Title]',
                country: '[Test Country]',
                senderName: sender?.displayName || '',
                senderEmail: sender?.email || '',
            };
            try {
                await authFetch('/reply', {
                    method: 'POST',
                    body: JSON.stringify({
                        toEmail: manualToEmail,
                        fromEmail,
                        subject: renderTemplate(subject, vars),
                        bodyText: renderTemplate(body, vars),
                    }),
                });
                setSendResults([{ email: manualToEmail, ok: true }]);
            } catch {
                setSendResults([{ email: manualToEmail, ok: false }]);
            }
            setSendProgress(1);
            setStep('done');
            return;
        }

        if (selectedContacts.length === 0 || !subject || !body || !fromEmail) return;
        setStep('sending');
        setSendTotal(selectedContacts.length);
        setSendProgress(0);
        setSendResults([]);

        for (let i = 0; i < selectedContacts.length; i++) {
            const contact = selectedContacts[i];
            const lang = getCountryLanguage(contact.country);
            const vars: Record<string, string> = {
                name: contact.name,
                company: contact.companyName,
                title: contact.title,
                country: contact.country,
                senderName: sender?.displayName || '',
                senderEmail: sender?.email || '',
            };
            try {
                await authFetch('/reply', {
                    method: 'POST',
                    body: JSON.stringify({
                        leadId: contact.id,
                        toEmail: contact.email,
                        fromEmail,
                        subject: renderTemplate(subject, vars),
                        bodyText: renderTemplate(body, vars),
                        targetLanguage: isEnglishCountry(contact.country) ? undefined : lang.code,
                    }),
                });
                setSendResults(prev => [...prev, { email: contact.email, ok: true, lang: lang.nativeName }]);
            } catch {
                setSendResults(prev => [...prev, { email: contact.email, ok: false }]);
            }
            setSendProgress(i + 1);
            // Small delay between sends
            if (i < selectedContacts.length - 1) {
                await new Promise(r => setTimeout(r, 300));
            }
        }
        setStep('done');
    };

    const previewVars = selectedContacts[previewIdx] ? {
        name: selectedContacts[previewIdx].name,
        company: selectedContacts[previewIdx].companyName,
        title: selectedContacts[previewIdx].title,
        country: selectedContacts[previewIdx].country,
        senderName: sender?.displayName || '',
        senderEmail: sender?.email || '',
    } : {
        name: '[Name]', company: '[Company]', title: '[Title]',
        country: '[Country]', senderName: sender?.displayName || '', senderEmail: sender?.email || '',
    };

    return (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}>
            <div className="modal-panel" style={{ width: step === 'select' ? 900 : 720, maxHeight: '85vh', overflow: 'auto' }}>

                {/* ── Header ── */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
                            {step === 'select' && "📋 选择收件人"}
                            {step === 'compose' && "✉️ 编辑邮件"}
                            {step === 'preview' && `👁️ 预览（${previewIdx + 1}/${selectedContacts.length}）`}
                            {step === 'sending' && `📤 Sending... ${sendProgress}/${sendTotal}`}
                            {step === 'done' && "✅ 发送完成"}
                        </h2>
                        {step === 'select' && (
                            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                                选择需要批量发送邮件的联系人。 {selectedIds.size > 0 && `${selectedIds.size} selected`}
                            </p>
                        )}
                    </div>
                    {step !== 'sending' && (
                        <button className="btn btn-outline btn-sm" onClick={onClose}>✕ 关闭</button>
                    )}
                </div>

                {/* ═══════════════════════════════ STEP 1: SELECT ═══════════════════════════════ */}
                {step === 'select' && (
                    <>
                        {/* Quick filters */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input className="input" style={{ flex: 1, minWidth: 180 }}
                                placeholder="🔍 搜索联系人…"
                                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

                            <select className="input" style={{ width: 'auto' }} value={countryFilter}
                                onChange={(e) => setCountryFilter(e.target.value)}>
                                <option value="all">🌍 全部国家</option>
                                {countries.map(c => <option key={c} value={c}>{REGION_ICONS[c] || '🌍'} {c}</option>)}
                            </select>

                            <select className="input" style={{ width: 'auto' }} value={campaignFilter}
                                onChange={(e) => setCampaignFilter(e.target.value)}>
                                <option value="all">📂 全部活动</option>
                                {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>

                            <select className="input" style={{ width: 'auto' }} value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="all">全部状态</option>
                                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>

                        {/* Quick select buttons */}
                        {countries.length > 0 && (
                            <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)', padding: '4px 0' }}>快速选择：</span>
                                {countries.map(c => (
                                    <button key={c} className="btn btn-outline btn-sm"
                                        style={{ fontSize: 10, padding: '2px 8px' }}
                                        onClick={() => selectByCountry(c)}>
                                        {REGION_ICONS[c] || '🌍'} 全部 {c}
                                    </button>
                                ))}
                                <button className="btn btn-outline btn-sm"
                                    style={{ fontSize: 10, padding: '2px 8px' }}
                                    onClick={toggleAll}>
                                    {selectedIds.size === filtered.length ? "☐ 取消全选" : "☑️ 选择当前全部联系人"}
                                </button>
                            </div>
                        )}

                        {/* Contact list */}
                        <div className="card" style={{ padding: 0, maxHeight: 360, overflow: 'auto' }}>
                            {loading ? (
                                <p style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>正在加载联系人…</p>
                            ) : filtered.length === 0 ? (
                                <p style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>未找到联系人</p>
                            ) : (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 32 }}>
                                                <input type="checkbox" className="checkbox"
                                                    checked={filtered.length > 0 && selectedIds.size === filtered.length}
                                                    onChange={toggleAll} />
                                            </th>
                                            <th>姓名</th>
                                            <th>职位</th>
                                            <th>公司</th>
                                            <th>邮箱</th>
                                            <th>国家</th>
                                            <th>语言</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map(l => (
                                            <tr key={l.id} style={{ cursor: 'pointer' }}
                                                onClick={() => toggleSelect(l.id)}>
                                                <td onClick={e => e.stopPropagation()}>
                                                    <input type="checkbox" className="checkbox"
                                                        checked={selectedIds.has(l.id)}
                                                        onChange={() => toggleSelect(l.id)} />
                                                </td>
                                                <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{l.name}</td>
                                                <td style={{ fontSize: 12 }}>{l.title}</td>
                                                <td style={{ fontSize: 12 }}>{l.companyName}</td>
                                                <td style={{ fontSize: 12, color: 'var(--accent-light)' }}>{l.email}</td>
                                                <td style={{ fontSize: 12 }}>{REGION_ICONS[l.country] || '🌍'} {l.country}</td>
                                                <td>{(() => {
                                                    const lang = getCountryLanguage(l.country); return (
                                                        <span style={{
                                                            fontSize: 10, padding: '2px 5px', borderRadius: 4,
                                                            background: isEnglishCountry(l.country) ? 'transparent' : 'rgba(168,85,247,0.15)',
                                                            color: isEnglishCountry(l.country) ? 'var(--text-muted)' : '#c084fc',
                                                        }}>
                                                            {isEnglishCountry(l.country) ? 'EN' : lang.nativeName}
                                                        </span>
                                                    );
                                                })()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Region lock warning */}
                        {isMixedRegion && selectedIds.size > 0 && (
                            <div style={{
                                marginTop: 10, padding: '8px 12px', background: 'rgba(245,158,11,0.1)',
                                border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8,
                                fontSize: 12, color: '#f59e0b',
                            }}>
                                ⚠️ 所选联系人来自多个地区，发件账号将受地区匹配规则限制。
                            </div>
                        )}

                        {/* Bottom: Next step */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                {filtered.length} 位联系人 · {selectedIds.size} 位已选择
                                {commonRegion && ` · 🔒 ${getRegionLabel(commonRegion)}`}
                            </span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-outline" onClick={() => {
                                    setTestMode(true);
                                    setStep('compose');
                                }}>
                                    🧪 发送测试邮件
                                </button>
                                <button className="btn btn-primary" onClick={() => {
                                    setTestMode(false);
                                    setStep('compose');
                                }}
                                    disabled={selectedIds.size === 0}>
                                    下一步：编辑邮件 →
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* ═══════════════════════════════ STEP 2: COMPOSE ═══════════════════════════════ */}
                {step === 'compose' && (
                    <>
                        {/* Summary bar */}
                        {testMode ? (
                            <div style={{
                                padding: '10px 14px', background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.25)',
                                borderRadius: 8, marginBottom: 16,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#c084fc' }}>🧪 测试邮件模式</span>
                                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>— 向指定邮箱发送一封测试邮件</span>
                                </div>
                                <div>
                                    <label className="section-title">收件邮箱</label>
                                    <input className="input" value={manualToEmail}
                                        onChange={(e) => setManualToEmail(e.target.value)}
                                        placeholder="例如：test@example.com"
                                        type="email" />
                                </div>
                            </div>
                        ) : (
                            <div style={{
                                padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 8,
                                marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}>
                                <div>
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>📤 {selectedContacts.length} 位收件人</span>
                                    {commonRegion && (
                                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--accent-light)' }}>
                                            🔒 {getRegionLabel(commonRegion)}
                                        </span>
                                    )}
                                </div>
                                {hasNonEnglish && (
                                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                        <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>🌐 自动翻译：</span>
                                        {targetLangs.filter(l => l.code !== 'en').map(l => (
                                            <span key={l.code} style={{
                                                fontSize: 10, padding: '2px 6px', borderRadius: 4,
                                                background: 'rgba(168,85,247,0.15)', color: '#c084fc', fontWeight: 600,
                                            }}>
                                                {l.flag} {l.nativeName}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* From */}
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">发件人</label>
                            <select className="input" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)}>
                                {(commonRegion ? allowedSenders : allSenders).map(s => (
                                    <option key={s.email} value={s.email}>{s.displayName} &lt;{s.email}&gt;</option>
                                ))}
                            </select>
                        </div>

                        {/* Subject */}
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">邮件主题</label>
                            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)}
                                placeholder="例如：致 {{company}} 的 {{name}} 的合作邀请" />
                        </div>

                        {/* Body */}
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">邮件正文</label>
                            <textarea className="input" value={body} onChange={(e) => setBody(e.target.value)}
                                rows={10} style={{ resize: 'vertical' }}
                                placeholder={`Hi {{name}},\n\nI came across {{company}} and wanted to reach out.\n\n[Your value proposition here]\n\nBest,\n{{senderName}}`} />
                        </div>

                        {/* Placeholder hints */}
                        <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {PLACEHOLDERS.map(p => (
                                <span key={p} style={{
                                    padding: '2px 8px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)',
                                    fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer',
                                }}
                                    onClick={() => setBody(prev => prev + p)}
                                >
                                    {p}
                                </span>
                            ))}
                        </div>

                        {hasNonEnglish && (
                            <div style={{
                                padding: '8px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
                                borderRadius: 8, marginBottom: 16, fontSize: 11, color: '#fbbf24',
                            }}>
                                💡 请用英文撰写邮件，系统会自动翻译为 {targetLangs.filter(l => l.code !== 'en').map(l => l.nativeName).join(', ')} ，供相应国家的收件人阅读。
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                            <button className="btn btn-outline" onClick={() => { setTestMode(false); setStep('select'); }}>← 返回</button>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {!testMode && (
                                    <button className="btn btn-outline" onClick={() => { setPreviewIdx(0); setStep('preview'); }}
                                        disabled={!subject || !body}>
                                        👁️ 预览
                                    </button>
                                )}
                                <button className="btn btn-primary btn-lg" onClick={autoSend}
                                    disabled={!fromEmail || !subject || !body || (testMode && !manualToEmail)}>
                                    {testMode
                                        ? "🧪 发送测试邮件"
                                        : `📤 发送给 ${selectedContacts.length} 位联系人`
                                    }
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* ═══════════════════════════════ STEP 3: PREVIEW ═══════════════════════════════ */}
                {step === 'preview' && selectedContacts[previewIdx] && (
                    <>
                        <div style={{
                            padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 8,
                            marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <div style={{ fontSize: 13 }}>
                                <strong>{selectedContacts[previewIdx].name}</strong>
                                <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>
                                    {selectedContacts[previewIdx].title} @ {selectedContacts[previewIdx].companyName}
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                {!isEnglishCountry(selectedContacts[previewIdx].country) && (
                                    <span style={{
                                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                                        background: 'rgba(168,85,247,0.15)', color: '#c084fc', fontWeight: 600,
                                    }}>
                                        🌐 → {getCountryLanguage(selectedContacts[previewIdx].country).nativeName}
                                    </span>
                                )}
                                <button className="btn btn-outline btn-sm" disabled={previewIdx === 0}
                                    onClick={() => setPreviewIdx(i => i - 1)}>←</button>
                                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                    {previewIdx + 1} / {selectedContacts.length}
                                </span>
                                <button className="btn btn-outline btn-sm" disabled={previewIdx === selectedContacts.length - 1}
                                    onClick={() => setPreviewIdx(i => i + 1)}>→</button>
                            </div>
                        </div>

                        <div style={{ marginBottom: 8 }}>
                            <label className="section-title">邮件主题</label>
                            <div style={{
                                padding: '8px 12px', background: 'var(--bg-input)', borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border-default)', fontSize: 13, fontWeight: 600,
                            }}>
                                {renderTemplate(subject, previewVars)}
                            </div>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label className="section-title">邮件正文</label>
                            <div style={{
                                padding: 16, background: 'var(--bg-input)', borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border-default)', fontSize: 13,
                                whiteSpace: 'pre-wrap', color: 'var(--text-primary)', minHeight: 160,
                            }}>
                                {renderTemplate(body, previewVars)}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                            <button className="btn btn-outline" onClick={() => setStep('compose')}>← 编辑</button>
                            <button className="btn btn-primary btn-lg" onClick={autoSend}>
                                📤 确认并发送至 {selectedContacts.length} 位联系人
                            </button>
                        </div>
                    </>
                )}

                {/* ═══════════════════════════════ STEP 4: SENDING ═══════════════════════════════ */}
                {step === 'sending' && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{
                            width: '100%', height: 8, background: 'var(--bg-elevated)',
                            borderRadius: 999, overflow: 'hidden', marginBottom: 16,
                        }}>
                            <div style={{
                                width: `${(sendProgress / sendTotal) * 100}%`,
                                height: '100%', borderRadius: 999,
                                background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
                                transition: 'width 0.3s ease',
                            }} />
                        </div>
                        <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                            正在发送 {sendProgress}／{sendTotal}…
                        </p>

                        {/* Live results */}
                        <div style={{ maxHeight: 200, overflowY: 'auto', textAlign: 'left' }}>
                            {sendResults.map((r, i) => (
                                <div key={i} style={{
                                    padding: '4px 12px', fontSize: 11, display: 'flex',
                                    justifyContent: 'space-between', alignItems: 'center',
                                    borderBottom: '1px solid var(--border-subtle)',
                                }}>
                                    <span>{r.ok ? '✅' : '❌'} {r.email}</span>
                                    {r.lang && <span style={{ color: 'var(--text-muted)' }}>→ {r.lang}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════ STEP 5: DONE ═══════════════════════════════ */}
                {step === 'done' && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>发送完成</h3>
                        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                            共 {sendResults.length} 封邮件，成功发送 {sendResults.filter(r => r.ok).length} 封
                        </p>

                        {sendResults.some(r => !r.ok) && (
                            <div style={{
                                padding: '8px 12px', background: 'rgba(239,68,68,0.1)',
                                border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8,
                                fontSize: 12, color: '#ef4444', marginBottom: 16, textAlign: 'left',
                            }}>
                                失败： {sendResults.filter(r => !r.ok).map(r => r.email).join(', ')}
                            </div>
                        )}

                        <button className="btn btn-primary" onClick={onClose}>关闭</button>
                    </div>
                )}
            </div>
        </div>
    );
}
